import { formatUtcCalendarDate } from "@utils/formatter";

// A promotion's dates are UTC, and the API's window is half-open:
// [startsAt, endsAt), so the end is the first moment it no longer shows. The
// admin works in calendar days and picks the LAST DAY it shows, so a picked
// last day is sent as the next day at 00:00 UTC, and a stored midnight end is
// shown as the day before. Everything here works on the string's own
// calendar date, never through local time (see formatUtcCalendarDate).

// A stored value at exactly midnight: "T00:00:00", optional fractional
// seconds, then "Z", a zero offset, or no zone (the API's dates are UTC).
const MIDNIGHT_UTC = /T00:00:00(?:\.0+)?(?:Z|[+-]00:?00)?$/;

// yyyy-MM-dd, `days` on. Date.UTC rolls month and year lengths over.
const shiftCalendarDate = (calendarDate: string, days: number): string => {
    const [year, month, day] = calendarDate.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day + days))
        .toISOString()
        .slice(0, 10);
};

const atMidnightUtc = (calendarDate: string): string =>
    `${calendarDate}T00:00:00Z`;

// The stored start's own UTC date, as an <input type="date"> takes it.
export const startToInput = (startsAt: string | null): string =>
    startsAt ? startsAt.slice(0, 10) : "";

// The last day a stored end leaves the promotion showing: the day before a
// midnight end; any other stored time is shown as its own date.
export const lastDayToInput = (endsAt: string | null): string => {
    if (!endsAt) {
        return "";
    }
    const calendarDate = endsAt.slice(0, 10);
    return MIDNIGHT_UTC.test(endsAt)
        ? shiftCalendarDate(calendarDate, -1)
        : calendarDate;
};

// A date the admin left as it was goes back exactly as stored, so editing
// the label never moves a stored 08:00 start to midnight.
export const startToApi = (
    input: string,
    stored: string | null,
): string | null => {
    if (input === "") {
        return null;
    }
    if (stored && startToInput(stored) === input) {
        return stored;
    }
    return atMidnightUtc(input);
};

export const lastDayToApi = (
    input: string,
    storedEnd: string | null,
): string | null => {
    if (input === "") {
        return null;
    }
    if (storedEnd && lastDayToInput(storedEnd) === input) {
        return storedEnd;
    }
    return atMidnightUtc(shiftCalendarDate(input, 1));
};

// The first and last day a promotion shows, for the table.
export const formatPromotionWindow = (
    startsAt: string | null,
    endsAt: string | null,
): string => {
    const first = startsAt ? formatUtcCalendarDate(startsAt) : null;
    const last = endsAt ? formatUtcCalendarDate(lastDayToInput(endsAt)) : null;

    if (first && last) {
        return `${first} – ${last}`;
    }
    if (first) {
        return `From ${first}`;
    }
    if (last) {
        return `Through ${last}`;
    }
    return "Always";
};
